import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LockIcon from '@mui/icons-material/Lock';
import VideocamIcon from '@mui/icons-material/Videocam';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { ScheduleInterviewModalProps } from './types';

export const ScheduleInterviewModal: React.FC<ScheduleInterviewModalProps> = ({
  open,
  caseDisplayId,
  scheduling,
  onClose,
  onSendInvitations,
}) => {
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  const derivedEmails = scheduling.witnesses
    .map((w) => w.email ?? '')
    .filter(Boolean)
    .join(';');
  const [emailTo, setEmailTo] = useState(derivedEmails);

  const hasMissingEmail = scheduling.witnesses.some((w) => !w.email);

  const handleSend = () => {
    const body = selectedTime
      ? scheduling.message.replace(
          '[PROPOSED DATE] at [PROPOSED TIME]',
          selectedTime,
        )
      : scheduling.message;
    const mailtoUrl = `mailto:${encodeURIComponent(emailTo)}?subject=${encodeURIComponent(scheduling.subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl, '_blank');
    onSendInvitations(selectedTime ?? undefined);
    setSelectedTime(null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      sx={{
        zIndex: 2100,
        '& .MuiDialog-container': {
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      PaperProps={{
        sx: { width: '620px', maxWidth: '620px', m: 'auto' },
      }}
    >
      <DialogTitle
        sx={{ px: 3, pt: 3, pb: 1, position: 'relative', textAlign: 'left' }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            pr: 5,
          }}
        >
          <CalendarTodayIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Schedule Confidential Interview
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close schedule interview"
          data-testid="schedule-interview-modal-close"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            border: 1,
            borderColor: 'primary.main',
            borderRadius: '8px',
            color: 'primary.main',
            '&:hover': { bgcolor: 'primary.main', color: 'common.white' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Outlook calendar integration for Case {caseDisplayId}
        </Typography>
      </DialogTitle>

      <DialogContent
        dividers={false}
        sx={{
          px: 3,
          pb: 3,
          pt: '24px !important',
          borderTop: 'none',
          borderBottom: 'none',
          margin: '5px 10px',
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            fontWeight={600}
            color="text.secondary"
          >
            To
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={scheduling.witnesses
              .map(
                (witness) =>
                  `${witness.name}${witness.role ? ` (${witness.role})` : ''}`,
              )
              .join('; ')}
            disabled
            sx={{ mt: 1 }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            fontWeight={600}
            color="text.secondary"
          >
            Email Address
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            disabled={!hasMissingEmail}
            placeholder={hasMissingEmail ? 'Enter email address(es)' : ''}
            helperText={
              hasMissingEmail
                ? 'No email on record — enter address(es) separated by semicolons'
                : undefined
            }
            inputProps={{ 'data-testid': 'schedule-interview-email-input' }}
            sx={{ mt: 1 }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            fontWeight={600}
            color="text.secondary"
          >
            Subject
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={scheduling.subject}
            disabled
            sx={{ mt: 1 }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            fontWeight={600}
            color="text.secondary"
          >
            Location
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <LockIcon fontSize="small" color="action" />
            <VideocamIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Secure Microsoft Teams Link Generated
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            fontWeight={600}
            color="text.secondary"
          >
            Message
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            value={scheduling.message}
            disabled
            sx={{ mt: 1 }}
          />
        </Box>

        {scheduling.suggestedTimes && scheduling.suggestedTimes.length > 0 && (
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <AccessTimeIcon fontSize="small" color="action" />
              <Typography
                variant="overline"
                fontWeight={600}
                color="text.secondary"
              >
                Suggested Times (High Availability)
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {scheduling.suggestedTimes.map((slot, idx) => {
                const value = `${slot.day}-${slot.time}`;
                const isSelected = selectedTime === value;
                return (
                  <Box
                    key={idx}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isSelected}
                    aria-label={`${slot.day} ${slot.time}, ${slot.duration}`}
                    data-testid={`investigation-suggested-time-${idx}`}
                    onClick={() => setSelectedTime(value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedTime(value);
                      }
                    }}
                    sx={{
                      flex: 1,
                      cursor: 'pointer',
                      border: 1,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      boxShadow: isSelected
                        ? (theme) => `0 0 0 1px ${theme.palette.primary.main}`
                        : 'none',
                      borderRadius: '8px',
                      p: 2,
                      textAlign: 'center',
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                      '&:hover': { borderColor: 'primary.main' },
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {slot.day}
                    </Typography>
                    <Typography variant="h6" fontWeight={600} sx={{ my: 0.5 }}>
                      {slot.time}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {slot.duration}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Button
              size="small"
              sx={{ mt: 2, textTransform: 'none', pl: 0 }}
              onClick={() => {
                /* Open manual date/time picker */
              }}
            >
              Manual Date/Time Selection
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={
            !emailTo.trim() ||
            (scheduling.suggestedTimes !== undefined &&
              scheduling.suggestedTimes.length > 0 &&
              !selectedTime)
          }
          sx={{ minWidth: 140 }}
        >
          Send Invitations
        </Button>
      </DialogActions>
    </Dialog>
  );
};
